import React from 'react';

interface IProgressBarComponentProps {
  completed: number;
  maxCompleted: number;

  bgColor?: string; // Color of the completed bar
  baseBgColor?: string; // Color of the "non-completed" bar
  borderRadius?: string;
  height?: string;
  width?: string;
  direction?: string; // ltr, rtl, auto
  margin?: string;
  padding?: string;

  customLabel?: string;
  isLabelVisible?: boolean;
  labelAlignment?: string; // left, center, right, outside
  labelColor?: string;
  labelSize?: string;
  labelClassName?: string;

  wrapperClassName?: string; // Add a className to the parent div
  containerClassName?: string;
  completedClassName?: string;

  startProgressElement?: React.ReactNode;
  endProgressElement?: React.ReactNode;
}

const ProgressBarComponent = ({
  completed,
  maxCompleted = 100,
  bgColor = '#093868',
  baseBgColor = 'rgba(87, 199, 133, 0.4)',
  borderRadius = '0',
  height = '2rem',
  width = '100%',
  direction = 'ltr',
  margin,
  padding,
  customLabel,
  isLabelVisible = false,
  labelAlignment = 'right',
  labelColor = '#fff',
  labelSize = '1.2rem',
  labelClassName,
  wrapperClassName,
  containerClassName,
  completedClassName,
  startProgressElement,
  endProgressElement,
}: IProgressBarComponentProps) => {
  const getAlignment = (alignmentOption: string) => {
    switch (alignmentOption) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      case 'center':
        return 'center';
      default:
        return null;
    }
  };

  const getCompletedWidth = (maxCompletedValue: number, completedValue: number) => {
    if (maxCompletedValue) {
      const ratio = Number(completedValue) / Number(maxCompletedValue);
      return ratio > 1 ? '100%' : `${ratio * 100}%`;
    }
    return 0;
  };

  const alignment = getAlignment(labelAlignment);
  const completedWidth = getCompletedWidth(maxCompleted, completed);

  const defaultContainerStyles = {
    height: height,
    background: baseBgColor,
    borderRadius: borderRadius,
    padding: padding,
    width: width,
    margin: margin,
    overflow: 'hidden',
  };
  const defaultCompletedStyles = {
    height: height,
    width: completedWidth,
    background: bgColor,
    transition: 'width 1s ease-in-out',
    borderRadius: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: labelAlignment !== 'outside' && alignment ? alignment : 'normal',
    animation: 'none',
  };
  const defaultLabelStyles = {
    padding: labelAlignment === 'outside' ? '0 0 0 5px' : '5px',
    color: labelColor,
    fontWeight: 'bold',
    fontSize: labelSize,
    display: !isLabelVisible ? 'none' : 'initial',
  };
  const defaultWrapperStyles = {
    display: labelAlignment === 'outside' ? 'flex' : 'initial',
    alignItems: labelAlignment === 'outside' ? 'center' : 'initial',
  };
  const labelStr = customLabel ? customLabel : String(completed);

  return (
    <div
      dir={direction}
      role="progressbar"
      aria-valuenow={completed}
      style={wrapperClassName ? undefined : defaultWrapperStyles}
      className={wrapperClassName}>
      {startProgressElement && startProgressElement}
      <div style={containerClassName ? undefined : defaultContainerStyles} className={containerClassName}>
        <div
          style={completedClassName ? { width: completedWidth } : defaultCompletedStyles}
          className={completedClassName}>
          <span
            style={labelClassName ? undefined : defaultLabelStyles}
            className={labelClassName}
            dangerouslySetInnerHTML={{
              __html: labelStr,
            }}></span>
        </div>
      </div>
      {endProgressElement && endProgressElement}
    </div>
  );
};

export default ProgressBarComponent;
